/**
 * A small, self-contained client for the typed relation graph, chains and clusters.
 *
 * Deliberately not added to `lib/api.ts`: that file is under concurrent, active edit
 * by the review-surface-integrity change, and this module's own fetch wrapper is a
 * dozen lines — cheaper to duplicate than to risk a merge collision on a shared file
 * neither change strictly needs the other's half of.
 */

export interface RelationEdge {
  relation_id: string;
  subject_cluster: string;
  object_cluster: string;
  relation_source: "declared" | "extracted";
  review_state: "proposed" | "confirmed" | "rejected";
  relation_type: string | null;
  is_claim: boolean;
  span_id: string;
  cluster_key: string | null;
  cluster_id: string | null;
  block_id: string | null;
  start_cp: number | null;
  end_cp: number | null;
  quote: string | null;
  path_offsets: [number, number][];
  // trigger-relations design D4/D6: null for a declared relation or a dependency-path
  // instance (attrs carries no method/direction/trigger key yet); populated for a
  // trigger-produced one. `direction` is `'surface_order'` (render undirected,
  // regardless of which of subject_cluster/object_cluster reads first) or
  // `'case_marked'` (the ending-role table found an unambiguous reading — render the
  // real subject -> object arrow).
  method: string | null;
  direction: "surface_order" | "case_marked" | null;
  trigger_offsets: [number, number][];
}

export interface TypedNode {
  cluster_id: string;
  display_value: string | null;
}

export interface TypedGraph {
  cluster_run_id: string | null;
  nodes: TypedNode[];
  edges: RelationEdge[];
}

export interface Chain {
  hops: RelationEdge[];
}

export interface ChainsResult {
  source: string;
  target: string;
  max_hops: number;
  cluster_run_id: string | null;
  chains: Chain[];
}

export interface PinChainResult {
  version_id: string;
  version_no: number;
  chain_unit_id: string;
  hop_unit_ids: string[];
}

class RelationsApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail);
    this.name = "RelationsApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: HeadersInit = { "Content-Type": "application/json", ...(init?.headers ?? {}) };
  const response = await fetch(path, { ...init, headers });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === "string") detail = body.detail;
    } catch {
      // A non-JSON error body is still an error; the status text stands.
    }
    throw new RelationsApiError(response.status, detail);
  }
  return (await response.json()) as T;
}

export const relationsApi = {
  typedGraph: (datasetId: string) =>
    request<TypedGraph>(`/api/datasets/${datasetId}/graph/relations`),

  chains: (datasetId: string, source: string, target: string, maxHops: number) =>
    request<ChainsResult>(
      `/api/datasets/${datasetId}/graph/relations/chains` +
        `?source=${source}&target=${target}&max_hops=${maxHops}`,
    ),

  pinChain: (
    datasetId: string,
    artifactId: string,
    body: { source: string; target: string; max_hops: number; chain_index: number },
  ) =>
    request<PinChainResult>(
      `/api/datasets/${datasetId}/artifacts/${artifactId}/pin-chain`,
      { method: "POST", body: JSON.stringify(body) },
    ),
};
